// Modules
import { isEmpty, rangeRight } from 'lodash';
import DOMPurify from 'dompurify';
import React, { Fragment, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { CellProps } from 'react-table';

// Components
import CutlineText from '../../components/CutlineText/CutlineText';
import Message from '../../components/Message/Message';
import ReactTable from '../../components/ReactTable/ReactTable';
import VideoPlayer, {
  IVideoPlayerContext,
  VideoPlayerContext,
} from '../../components/VideoPlayer/VideoPlayer';

// Config
import { AOY_START_YEAR, AoyTournamentSelect, CLASS_PREFIX, DEFAULT_STRING_NO_TABLE_RESULTS } from '../../config';

// Types
import {
  AdvertisementVariant,
  AoyTournamentType,
  IBasstrakkAngler,
  ICutline,
  IDataProps,
  IRowSeparator,

} from '../../types';

// Components
import AdWrapper from '../../components/Advertisement/AdWrapper';
import AOYExpanded from '../../components/AnglerExpanded/AOYExpanded';
import AoyTableRenderRow from '../BasstrakkTable/AoyTableRenderRow';

// Table Columns
import { AOYColumns } from '../BasstrakkTable/AOYColumns';

// Get Historical Data
import { useAoyHistoric } from '../../hooks';

// import TabContext from '../../components/Context';
import { TabProvider } from '../../components/Context';
import RowSeparator from '../../components/TableRow/RowSeparator';

const videoPlayerId = 'bm-video-player';

interface IAoyTable {
  cutlines: ICutline[];
  getKayakContent: Function;
  kayakContent: string;
  tabLabel: string;
  tabClass: string;
  type: AoyTournamentType;
  year: number;
  updateYear: (year: number, type?: number) => void;
}

/**
 * Angler of the Year
 */
const AoyFull = (props: IAoyTable) => {
  const {
    cutlines: basstrakkCutlines,
    getKayakContent,
    kayakContent,
    tabClass,
    tabLabel,
    updateYear,
    year,
  } = props;

  // Cutlines.
  const [cutlines] = useState<ICutline[]>(
    !isEmpty(basstrakkCutlines) ? basstrakkCutlines : []
  );

  // Set year on selector change.
  const selectYearChange = (value) => {
    updateYear(value);
  };

  // Set type on selector change.
  const [type, setType] = useState(props.type);
  const selectTypeChange = (value) => {
    setType(value);
  };

  // Get the data
  const data = useAoyHistoric(type, year);

  // Video realted.
  const [videoId, setVideoId] = useState<number | null>(null);
  const [videoPlayerNode, setVideoPlayerNode] = useState(
    document.getElementById(videoPlayerId) || null
  );
  const [hasVideoPlayer, setHasVideoPlayer] = useState<boolean>(false);
  const [isVideoPlaying] = useState<boolean>(false);

    // Get the Basstrakk Table Columns
  const headerColumns = useMemo(() => AOYColumns, []);

  /**
   * Injects a div at the end of the document.body so that the React Portal can be created
   */
  const onVideoSelect = (selectedVideoId: number) => {
    let videoPlayerNodeById = document.getElementById(
      videoPlayerId
    ) as HTMLDivElement;

    if (!videoPlayerNodeById) {
      videoPlayerNodeById = document.createElement('div');
      videoPlayerNodeById.id = videoPlayerId;
      document.body.appendChild(videoPlayerNodeById);
    }

    setVideoId(selectedVideoId);
    setVideoPlayerNode(videoPlayerNodeById);
    setHasVideoPlayer(true);
  };

  const setContext = (newContext: Partial<IVideoPlayerContext>) => {
    const {
      videoId: videoIdFromContext,
      hasVideoPlayer: hasVideoPlayerFromContext,
    } = newContext;
    if (videoIdFromContext) {
      onVideoSelect(videoIdFromContext);
      return;
    }

    // Key must be false, if it's undefined skip
    if (hasVideoPlayerFromContext === false) {
      setHasVideoPlayer(false);
      return;
    }
  };

  /**
   * Use to determine when a banner ad should display
   *
   * This does not include banner ads that should display with cutlines
   * @param index
   */
  const shouldRenderBanner = index => {
    // @todo: Add config in Drupal to allow passing these hardcoded values to
    // the frontend
    const targetRows = [30, 70];

    if (index !== 0 && targetRows.includes(index)) {
      return true;
    }
    return false;
  };

  // Renders a row in the table
  const renderRow = ({ row, index }: CellProps<IDataProps>) => {
    return <AoyTableRenderRow row={row} index={index} />;
  };

  // Allows rendering an "expanded" row below a default table row.
  const renderExpanded = ({ row, index }: CellProps<IDataProps>) => {
    const { original: angler } = row;
    return (
      <AOYExpanded
        anglerId={angler.tms}
        anglerName={angler.name}
        count={index}
        year={year}
        type={type}
      />
    );
  };

  const currentYear = new Date().getFullYear();
  const rowVariant:any = `${AdvertisementVariant.AOY}-${type}`;

  const renderRowSeparator = (type === 91 || type === 501) && currentYear === year && !isEmpty(cutlines) ?
  ({ row, index }: CellProps<IBasstrakkAngler>) => {
    return (
      <>
        {cutlines.map(cutline => {
          if (cutline.cutlineValue === index + 1) {
            return (
              <Fragment key={cutline.cutlineValue}>
                <div
                  className={`${
                    row.isExpanded ? 'cutline cutline--expanded' : 'cutline'
                  }`}
                  data-testid="cutline-row"
                >
                  <div style={{ width: '100%' }} className="cutline-text">
                    <CutlineText text={cutline.cutlineText} />
                  </div>
                </div>
                <AdWrapper
                  count={cutline.cutlineValue}
                  variant={`${AdvertisementVariant.AOYCUTLINE}-${type}`}
                />
              </Fragment>
            );
          }
        })}
        {shouldRenderBanner(index + 1) && (
          <AdWrapper
            count={index + 1}
            variant={`${AdvertisementVariant.AOY}-${type}`}
          />
        )}
      </>
    );
  }
  :
  ({ numRows, index }: IRowSeparator) => (
    <RowSeparator
      numRows={numRows}
      index={index}
      variant={rowVariant}
    />
  );

  // We have a default message when there is no data
  const shouldShow = isEmpty(data);

  const AoySelectOptions = AoyTournamentSelect[type];

  if ('KAYAK' === tabLabel) {
    if (!kayakContent) {
      getKayakContent();
    } else {
      return (
        <div
          className="kayak-full-container"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(kayakContent) }}
        />
      );
    }
  }

  return (
    <>
      <div className={`aoy-selectors ${tabClass}`} id={`${tabClass}`}>
        <div className="aoy-year-selector">
          <span className="year-selector-label">Year:</span>
          <select
              id="select-year"
              value={year}
              className="aoy-standings-year form-control form-select"
              onChange={e => selectYearChange(e.target.value)}
            >
              {rangeRight(AOY_START_YEAR, currentYear + 1).map(yearSelect => (
                <option key={yearSelect} value={yearSelect}>
                  {yearSelect}
                </option>
              ))}
          </select>
        </div>
        { AoySelectOptions?.length > 1 ? (
          <div className="aoy-type-selector">
            <span className="type-selector-label">Type:</span>
            <select
                id="select-year"
                value={type}
                className="aoy-standings-type form-control form-select"
                onChange={e => selectTypeChange(e.target.value)}
              >
                {AoySelectOptions.map(typeSelect => (
                  <option key={typeSelect[0]} value={typeSelect[0]}>
                    {typeSelect[1]}
                  </option>
                ))}
            </select>
          </div>
        ) : null }
      </div>
      {shouldShow ? (
        <Message
          message={DEFAULT_STRING_NO_TABLE_RESULTS}
          customClass="no-results-message"
          shouldShow={shouldShow}
        />
      ) : (
        <>
          <TabProvider value={{ tab: 'aoy' }}>
            <VideoPlayerContext.Provider
              value={{
                hasVideoPlayer,
                isVideoPlaying,
                setContext,
                videoId,
              }}
            >
              <ReactTable
                tableName="aoy"
                columns={headerColumns}
                data={data}
                renderExpanded={renderExpanded}
                renderRow={renderRow}
                renderRowSeparator={renderRowSeparator}
                classPrefix={CLASS_PREFIX}
                autoReset={true}
              />
              {hasVideoPlayer &&
                videoPlayerNode &&
                ReactDOM.createPortal(
                  <VideoPlayer hasAutoPlay />,
                  videoPlayerNode
                )}
            </VideoPlayerContext.Provider>
          </TabProvider>
        </>
      )}
    </>
  );
};

export default AoyFull;
